import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import type { DataTableColumnMeta } from './types';

// Text columns longer than this are truncated by default, with the full value shown on hover.
const DEFAULT_TRUNCATE_LENGTH = 50;

function TruncatedText({ value }: { value: string }) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span>{`${value.slice(0, DEFAULT_TRUNCATE_LENGTH)}…`}</span>
                </TooltipTrigger>
                <TooltipContent>{value}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

// --- Shared config types ---

type BaseColumnConfig<TData, TFilter> = {
    header: string;
    filterKey?: keyof TFilter & string;
    defaultVisible?: boolean;
    enableServerSort?: boolean;
} & (
    | { accessorKey: keyof TData & string; accessorFn?: never; id?: string }
    | { accessorFn: (row: TData) => unknown; id: string; accessorKey?: never }
);

function buildAccessorProps<TData>(config: {
    accessorKey?: keyof TData & string;
    accessorFn?: (row: TData) => unknown;
    id?: string;
}) {
    if (config.accessorKey) {
        return {
            accessorKey: config.accessorKey,
            ...(config.id ? { id: config.id } : {}),
        };
    }
    return { accessorFn: config.accessorFn, id: config.id };
}

// --- Text column ---

type TextColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    placeholder?: string;
    cell?: ColumnDef<TData, unknown>['cell'];
    // Long values are truncated (with a hover tooltip) by default. Set to false to always show the full text.
    truncate?: boolean;
};

export function textColumn<TData, TFilter>(config: TextColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'text',
            filterKey: config.filterKey,
            placeholder: config.placeholder,
        };
    }

    const shouldTruncate = config.truncate ?? true;
    const defaultCell: ColumnDef<TData, unknown>['cell'] = ({ getValue }) => {
        const rawValue = getValue();
        const value = rawValue == null ? '' : String(rawValue);
        if (shouldTruncate && value.length > DEFAULT_TRUNCATE_LENGTH) {
            return <TruncatedText value={value} />;
        }
        return <span>{value}</span>;
    };

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        cell: config.cell ?? defaultCell,
    };
}

// --- Boolean column ---

type BooleanColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function booleanColumn<TData, TFilter>(config: BooleanColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'boolean',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        cell:
            config.cell ??
            (({ getValue }) => {
                const value = getValue() as boolean | null | undefined;
                return value ? 'Oui' : 'Non';
            }),
    };
}

// --- Multi-select column ---

type MultiSelectColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    options: { value: string; label: string }[];
    renderOption?: (option: { value: string; label: string }) => ReactNode;
    cell: ColumnDef<TData, unknown>['cell'];
    defaultFilterValue?: string[];
};

export function multiSelectColumn<TData, TFilter>(
    config: MultiSelectColumnConfig<TData, TFilter>,
): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'multi-select',
            filterKey: config.filterKey,
            options: config.options,
            renderOption: config.renderOption,
            ...(config.defaultFilterValue ? { defaultValue: config.defaultFilterValue } : {}),
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        cell: config.cell,
        filterFn: (row, columnId, filterValue: string[]) => {
            if (!filterValue || filterValue.length === 0) return true;
            const cellValue = row.getValue(columnId) as string;
            return filterValue.includes(cellValue);
        },
    };
}

// --- Date column ---

type DateColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function dateColumn<TData, TFilter>(config: DateColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'date',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        cell:
            config.cell ??
            (({ getValue }) => {
                const value = getValue() as string | number | null | undefined;
                if (!value) return '';
                return new Date(value).toLocaleString('fr-FR');
            }),
    };
}

// --- Date range column ---

type DateRangeColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function dateRangeColumn<TData, TFilter>(
    config: DateRangeColumnConfig<TData, TFilter>,
): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'date-range',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        cell:
            config.cell ??
            (({ getValue }) => {
                const value = getValue() as string | number | null | undefined;
                if (!value) return '';
                return new Date(value).toLocaleString('fr-FR');
            }),
    };
}

// --- Number column ---

type NumberColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    placeholder?: string;
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function numberColumn<TData, TFilter>(config: NumberColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'number',
            filterKey: config.filterKey,
            placeholder: config.placeholder,
        };
    }

    return {
        ...buildAccessorProps(config),
        header: config.header,
        meta,
        ...(config.cell ? { cell: config.cell } : {}),
    };
}

// --- Number range column ---

type NumberRangeColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function numberRangeColumn<TData, TFilter>(
    config: NumberRangeColumnConfig<TData, TFilter>,
): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'number-range',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        ...(config.cell ? { cell: config.cell } : {}),
        filterFn: (row, columnId, filterValue: { gte?: number; lte?: number }) => {
            if (filterValue?.gte == null && filterValue?.lte == null) return true;
            const value = row.getValue<number>(columnId);
            if (typeof value !== 'number' || Number.isNaN(value)) return false;
            if (filterValue.gte != null && value < filterValue.gte) return false;
            if (filterValue.lte != null && value > filterValue.lte) return false;
            return true;
        },
    };
}
